import React from 'react';

/**
 * Email-Safe Ticket Component
 * 
 * Uses inline styles and table-based layout for maximum email client compatibility.
 * Tested for: Gmail, Outlook, Apple Mail, Yahoo, etc.
 * 
 * NO styled-components, NO flexbox/grid, NO SVG
 */
const EmailTicket = ({ 
  bookingReference,
  selectedTickets = [],
  selectedDate,
  selectedTimeSlot,
  customerDetails = {},
  totalAmount = 0,
  venposBasket,
  bookingSummary,
  isFamilySaver = false,
  qrCodeUrl = null // URL to QR code PNG image (must be absolute)
}) => {
  // Brand colors
  const colors = {
    navyPrimary: '#101d40',
    navyLight: '#1a2d5a',
    gold: '#9d6d49',
    greenBright: '#22c55e',
    greenLight: '#dcfce7',
    greenDark: '#166534',
    offWhite: '#f8f7f4',
    lightGray: '#f8f9fa',
    textGray: '#666666',
    white: '#ffffff'
  };

  const formatDate = (date) => {
    const d = new Date(date);
    return d.toLocaleDateString('en-GB', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (timeSlot) => {
    return timeSlot?.time || timeSlot || 'Not specified';
  };

  // Extract discount information
  const basketData = venposBasket?.data;
  const discounts = basketData?.discounts || bookingSummary?.discounts || [];
  const totalSavings = bookingSummary?.totalSavings || discounts.reduce((sum, d) => sum + (d.discountedAmount || 0), 0);
  const hasSavings = totalSavings > 0;
  
  // Detect discount type
  const isFamilySaverDiscount = discounts.some(d => d.code && (d.code.toUpperCase().startsWith('FS') || d.code.toUpperCase().startsWith('FAMILYSAVER')));
  const isTravelZooDiscount = discounts.some(d => d.code && d.code.toUpperCase().startsWith('TZ'));
  
  // Calculate prices
  let originalPrice, finalPrice;
  if (bookingSummary?.originalPrice && bookingSummary?.finalPrice !== undefined) {
    originalPrice = bookingSummary.originalPrice;
    finalPrice = bookingSummary.finalPrice;
  } else if (basketData?.totalPrice && basketData?.finalPrice !== undefined) {
    originalPrice = basketData.totalPrice;
    finalPrice = basketData.finalPrice;
  } else {
    originalPrice = totalAmount;
    finalPrice = totalAmount;
  }

  const totalTickets = selectedTickets.reduce((total, ticket) => total + (ticket.quantity || 0), 0);
  const hasChildTicket = selectedTickets.some(t => {
    if (!(t.quantity > 0)) return false;
    const n = (t.name || t.title || '').toLowerCase();
    return n.includes('child') || n.includes('junior') || n.includes('under ');
  });
  const lowerTicketNames = selectedTickets.map(t => (t.name || t.title || '').toLowerCase());
  const pageId = venposBasket?.data?.pageId || bookingSummary?.pageId || null;
  const isFlowerGardenShow = (pageId === '1ced1e1a-b442-4dee-b1e1-ae8b5b7bee09') ||
    lowerTicketNames.some(n => n.includes('flower') && n.includes('garden'));
  const isSafariBooking = pageId === '94fbd17d-c40c-49f3-878e-d0bc415926b2' || pageId === '6d614bac-7055-4932-b62b-c960e7ff213b';
  const isSafariCastle = pageId === '94fbd17d-c40c-49f3-878e-d0bc415926b2';

  // Use absolute URL for logo (production)
  const logoUrl = 'https://www.belvoircastle.com/wp/wp-content/uploads/2025/10/logo_stacked.png';

  return (
    <div style={{
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      maxWidth: '600px',
      margin: '0 auto',
      backgroundColor: colors.white
    }}>
      {/* Main Container */}
      <table cellPadding="0" cellSpacing="0" border="0" width="100%" style={{
        maxWidth: '600px',
        margin: '0 auto',
        backgroundColor: colors.white,
        border: `2px solid ${colors.navyPrimary}`
      }}>
        {/* Header */}
        <tr>
          <td style={{
            background: isSafariBooking
              ? 'linear-gradient(135deg, #0C2C1B 0%, #1B5E3B 100%)'
              : `linear-gradient(135deg, ${colors.navyPrimary} 0%, ${colors.navyLight} 100%)`,
            padding: '20px 30px',
            borderBottom: isSafariBooking ? '4px solid #C8861A' : `4px solid ${colors.gold}`
          }}>
            <table cellPadding="0" cellSpacing="0" border="0" width="100%">
              <tr>
                <td>
                  <table cellPadding="0" cellSpacing="0" border="0">
                    <tr>
                      <td style={{ paddingRight: '16px', verticalAlign: 'middle' }}>
                        <img 
                          src={logoUrl}
                          alt="Belvoir Castle"
                          width="50"
                          height="50"
                          style={{
                            display: 'block'
                          }}
                        />
                      </td>
                      <td style={{ verticalAlign: 'middle' }}>
                        <div style={{
                          color: colors.white,
                          fontSize: '24px',
                          fontWeight: '700',
                          letterSpacing: '2px',
                          fontFamily: 'Georgia, serif'
                        }}>
                          BELVOIR CASTLE
                        </div>
                        <div style={{
                          color: isSafariBooking ? '#C8861A' : 'rgba(255,255,255,0.9)',
                          fontSize: '13px',
                          letterSpacing: '1px',
                          marginTop: '4px',
                          fontWeight: isSafariBooking ? '600' : 'normal'
                        }}>
                          {isSafariBooking ? 'Summer Safari 2026' : 'Admission Ticket'}
                        </div>
                      </td>
                    </tr>
                  </table>
                </td>
              </tr>
            </table>
          </td>
        </tr>

        {/* Body */}
        <tr>
          <td style={{ padding: '20px' }}>
            
            {/* Booking Reference */}
            <table cellPadding="0" cellSpacing="0" border="0" width="100%" style={{
              backgroundColor: colors.offWhite,
              border: `2px solid ${colors.gold}`,
              borderRadius: '8px',
              marginBottom: '14px'
            }}>
              <tr>
                <td align="center" style={{ padding: '12px 16px' }}>
                  <div style={{
                    fontSize: '11px',
                    color: colors.textGray,
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                    marginBottom: '6px'
                  }}>
                    Booking Reference
                  </div>
                  <div style={{
                    fontSize: '20px',
                    fontWeight: '700',
                    letterSpacing: '2.5px',
                    fontFamily: '"Courier New", monospace',
                    color: colors.navyPrimary
                  }}>
                    {bookingReference}
                  </div>
                </td>
              </tr>
            </table>

            {/* Visit Details - Compact boxes */}
            <table cellPadding="0" cellSpacing="0" border="0" width="100%" style={{ marginBottom: '10px' }}>
              <tr>
                <td style={{
                  backgroundColor: colors.white,
                  padding: '10px 14px',
                  borderLeft: `3px solid ${colors.gold}`,
                  borderRadius: '6px'
                }}>
                  <table cellPadding="0" cellSpacing="0" border="0" width="100%">
                    <tr>
                      <td style={{
                        fontSize: '11px',
                        color: colors.textGray,
                        fontWeight: '600',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                        Visit Date
                      </td>
                      <td align="right" style={{
                        fontSize: '14px',
                        color: colors.navyPrimary,
                        fontWeight: '700'
                      }}>
                        {formatDate(selectedDate)}
                      </td>
                    </tr>
                  </table>
                </td>
              </tr>
              <tr><td style={{ height: '6px' }}></td></tr>
              <tr>
                <td style={{
                  backgroundColor: colors.white,
                  padding: '10px 14px',
                  borderLeft: `3px solid ${colors.gold}`,
                  borderRadius: '6px'
                }}>
                  <table cellPadding="0" cellSpacing="0" border="0" width="100%">
                    <tr>
                      <td style={{
                        fontSize: '11px',
                        color: colors.textGray,
                        fontWeight: '600',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                        Arrival Time
                      </td>
                      <td align="right" style={{
                        fontSize: '14px',
                        color: colors.navyPrimary,
                        fontWeight: '700'
                      }}>
                        {formatTime(selectedTimeSlot)}
                      </td>
                    </tr>
                  </table>
                </td>
              </tr>
              <tr><td style={{ height: '6px' }}></td></tr>
              <tr>
                <td style={{
                  backgroundColor: colors.white,
                  padding: '10px 14px',
                  borderLeft: `3px solid ${colors.gold}`,
                  borderRadius: '6px'
                }}>
                  <table cellPadding="0" cellSpacing="0" border="0" width="100%">
                    <tr>
                      <td style={{
                        fontSize: '11px',
                        color: colors.textGray,
                        fontWeight: '600',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                        Guest Name
                      </td>
                      <td align="right" style={{
                        fontSize: '14px',
                        color: colors.navyPrimary,
                        fontWeight: '700'
                      }}>
                        {customerDetails.firstName} {customerDetails.lastName}
                      </td>
                    </tr>
                  </table>
                </td>
              </tr>
              <tr><td style={{ height: '6px' }}></td></tr>
              <tr>
                <td style={{
                  backgroundColor: colors.white,
                  padding: '10px 14px',
                  borderLeft: `3px solid ${colors.gold}`,
                  borderRadius: '6px'
                }}>
                  <table cellPadding="0" cellSpacing="0" border="0" width="100%">
                    <tr>
                      <td style={{
                        fontSize: '11px',
                        color: colors.textGray,
                        fontWeight: '600',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                        Number of Guests
                      </td>
                      <td align="right" style={{
                        fontSize: '14px',
                        color: colors.navyPrimary,
                        fontWeight: '700'
                      }}>
                        {totalTickets} {totalTickets === 1 ? 'Guest' : 'Guests'}
                      </td>
                    </tr>
                  </table>
                </td>
              </tr>
              <tr><td style={{ height: '6px' }}></td></tr>
              <tr>
                <td style={{
                  backgroundColor: colors.white,
                  padding: '10px 14px',
                  borderLeft: `3px solid ${colors.gold}`,
                  borderRadius: '6px'
                }}>
                  <table cellPadding="0" cellSpacing="0" border="0" width="100%">
                    <tr>
                      <td style={{
                        fontSize: '11px',
                        color: colors.textGray,
                        fontWeight: '600',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                        Total Paid (inc. VAT)
                      </td>
                      <td align="right" style={{
                        fontSize: '14px',
                        color: colors.navyPrimary,
                        fontWeight: '700'
                      }}>
                        £{finalPrice.toFixed(2)}
                      </td>
                    </tr>
                  </table>
                </td>
              </tr>
            </table>

            {/* Safari Inclusions Strip */}
            {isSafariBooking && (
              <table cellPadding="0" cellSpacing="0" border="0" width="100%" style={{
                background: '#0C2C1B',
                borderRadius: '8px',
                marginBottom: '14px',
                marginTop: '10px'
              }}>
                <tr>
                  <td style={{ padding: '16px 20px' }}>
                    <div style={{
                      fontSize: '10px',
                      color: '#C8861A',
                      fontWeight: '700',
                      textTransform: 'uppercase',
                      letterSpacing: '1.5px',
                      marginBottom: '6px'
                    }}>
                      Belvoir Summer Safari · 13 July – 1 September 2026
                    </div>
                    <div style={{
                      fontSize: '15px',
                      color: '#ffffff',
                      fontWeight: '600',
                      fontFamily: 'Georgia, serif',
                      marginBottom: '12px',
                      lineHeight: '1.3'
                    }}>
                      Everything is included — just bring the explorers
                    </div>
                    <table cellPadding="0" cellSpacing="0" border="0" width="100%">
                      <tr>
                        <td style={{ width: '50%', paddingBottom: '6px' }}>
                          <span style={{ color: '#C8861A', fontWeight: '700', marginRight: '6px' }}>✓</span>
                          <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.82)' }}>Safari Trail map</span>
                        </td>
                        <td style={{ width: '50%', paddingBottom: '6px' }}>
                          <span style={{ color: '#C8861A', fontWeight: '700', marginRight: '6px' }}>✓</span>
                          <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.82)' }}>Animal Park entry</span>
                        </td>
                      </tr>
                      <tr>
                        {hasChildTicket && (
                          <td style={{ paddingBottom: '6px' }}>
                            <span style={{ color: '#C8861A', fontWeight: '700', marginRight: '6px' }}>✓</span>
                            <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.82)' }}>Free ice cream for children (trail completers)</span>
                          </td>
                        )}
                        <td style={{ paddingBottom: '6px' }}>
                          {isSafariCastle && (
                            <>
                              <span style={{ color: '#C8861A', fontWeight: '700', marginRight: '6px' }}>✓</span>
                              <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.82)' }}>Bertie the Bear castle trail</span>
                            </>
                          )}
                        </td>
                      </tr>
                    </table>
                  </td>
                </tr>
              </table>
            )}

            {/* Savings Banner - Matches print ticket */}
            {hasSavings && (
              <table cellPadding="0" cellSpacing="0" border="0" width="100%" style={{
                background: 'linear-gradient(to right, #f0fdf4, #dcfce7)',
                border: `2px solid #86efac`,
                borderLeft: `4px solid ${colors.greenBright}`,
                borderRadius: '8px',
                marginBottom: '14px',
                marginTop: '10px'
              }}>
                <tr>
                  <td align="center" style={{ padding: '14px 16px' }}>
                    <div style={{
                      display: 'inline-block',
                      backgroundColor: colors.greenBright,
                      color: colors.white,
                      padding: '4px 14px',
                      borderRadius: '12px',
                      fontSize: '11px',
                      fontWeight: '700',
                      textTransform: 'uppercase',
                      letterSpacing: '1px',
                      marginBottom: '6px'
                    }}>
                      {isFamilySaverDiscount 
                        ? 'FAMILY SAVER' 
                        : isTravelZooDiscount 
                          ? 'TRAVEL ZOO' 
                          : 'DISCOUNT'}
                    </div>
                    <div style={{
                      fontSize: '16px',
                      fontWeight: '600',
                      color: '#15803d',
                      marginTop: '6px'
                    }}>
                      You Saved <span style={{
                        fontSize: '22px',
                        fontWeight: '800',
                        color: '#16a34a'
                      }}>£{totalSavings.toFixed(2)}</span>
                    </div>
                  </td>
                </tr>
              </table>
            )}

            {/* QR Code Section - Matches print ticket */}
            <table cellPadding="0" cellSpacing="0" border="0" width="100%" style={{
              backgroundColor: colors.lightGray,
              border: `2px dashed ${colors.navyPrimary}`,
              borderRadius: '8px',
              marginBottom: '14px'
            }}>
              <tr>
                <td align="center" style={{ padding: '16px' }}>
                  <div style={{
                    fontSize: '12px',
                    color: colors.navyPrimary,
                    fontWeight: '700',
                    textTransform: 'uppercase',
                    letterSpacing: '0.8px',
                    marginBottom: '10px'
                  }}>
                    Present at Entrance
                  </div>
                  <div style={{
                    display: 'inline-block',
                    backgroundColor: colors.white,
                    border: `2px solid ${colors.navyPrimary}`,
                    borderRadius: '8px',
                    padding: '10px'
                  }}>
                    <img 
                      src={qrCodeUrl || `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(bookingReference)}`}
                      alt={`QR Code: ${bookingReference}`}
                      width="100"
                      height="100"
                      style={{
                        display: 'block'
                      }}
                    />
                  </div>
                  <div style={{
                    fontSize: '11px',
                    color: colors.textGray,
                    marginTop: '8px',
                    lineHeight: '1.3'
                  }}>
                    Scan code or show reference
                  </div>
                </td>
              </tr>
            </table>

            {/* Tickets List */}
            <table cellPadding="0" cellSpacing="0" border="0" width="100%" style={{ marginBottom: '16px' }}>
              <tr>
                <td>
                  <div style={{
                    fontSize: '14px',
                    color: colors.navyPrimary,
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    marginBottom: '12px'
                  }}>
                    Tickets
                  </div>
                  {selectedTickets.filter(ticket => ticket.quantity > 0).map((ticket, index) => {
                    // Use totalPrice if available (from grouped tickets), otherwise calculate
                    const ticketPrice = ticket.totalPrice || (ticket.price * ticket.quantity) || 0;
                    const ticketName = ticket.title || ticket.name || 'Ticket';
                    const isRetailVillageGrotto = ticketName.toLowerCase().includes("santa's grotto") && 
                                                   ticketName.toLowerCase().includes("retail village");
                    
                    return (
                      <React.Fragment key={index}>
                        <table cellPadding="0" cellSpacing="0" border="0" width="100%" style={{
                          borderBottom: index < selectedTickets.filter(t => t.quantity > 0).length - 1 ? '1px solid #e0e0e0' : 'none',
                          marginBottom: '4px'
                        }}>
                          <tr>
                            <td style={{
                              padding: '8px 0',
                              fontSize: '13px',
                              color: '#333333'
                            }}>
                              {ticket.quantity}x {ticketName}
                            </td>
                            <td align="right" style={{
                              padding: '8px 0',
                              fontSize: '13px',
                              fontWeight: '600',
                              color: colors.navyPrimary
                            }}>
                              £{ticketPrice.toFixed(2)}
                            </td>
                          </tr>
                        </table>
                        {isRetailVillageGrotto && (
                          <table cellPadding="0" cellSpacing="0" border="0" width="100%" style={{ marginBottom: '4px' }}>
                            <tr>
                              <td style={{
                                padding: '4px 0 4px 12px',
                                fontSize: '11px',
                                color: colors.textGray,
                                fontStyle: 'italic',
                                borderLeft: `2px solid ${colors.gold}`
                              }}>
                                This is a 5 minute walk from the castle
                              </td>
                            </tr>
                          </table>
                        )}
                      </React.Fragment>
                    );
                  })}
                </td>
              </tr>
            </table>

            {/* Family Saver Message */}
            {(isFamilySaver || isFamilySaverDiscount) && !isFlowerGardenShow && (
              <table cellPadding="0" cellSpacing="0" border="0" width="100%" style={{
                background: `linear-gradient(135deg, ${colors.navyPrimary} 0%, ${colors.navyLight} 100%)`,
                borderLeft: `4px solid ${colors.gold}`,
                borderRadius: '8px',
                marginBottom: '16px'
              }}>
                <tr>
                  <td align="center" style={{ padding: '16px 20px' }}>
                    <div style={{
                      color: colors.white,
                      fontSize: '15px',
                      fontWeight: '600',
                      marginBottom: '6px'
                    }}>
                      Thank You from the Duchess &amp; Team
                    </div>
                    <div style={{
                      color: 'rgba(255,255,255,0.95)',
                      fontSize: '13px',
                      fontStyle: 'italic',
                      lineHeight: '1.4'
                    }}>
                    
                    </div>
                  </td>
                </tr>
              </table>
            )}

            {/* Important Information */}
            <table cellPadding="0" cellSpacing="0" border="0" width="100%" style={{
              backgroundColor: colors.offWhite,
              border: `1px solid ${colors.gold}`,
              borderRadius: '6px',
              marginBottom: '16px'
            }}>
              <tr>
                <td style={{ padding: '16px' }}>
                  <div style={{
                    fontSize: '13px',
                    color: colors.navyPrimary,
                    fontWeight: '700',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    marginBottom: '10px'
                  }}>
                    Information
                  </div>
                  <div style={{
                    fontSize: '12px',
                    color: '#333333',
                    lineHeight: '1.6'
                  }}>
                    {isSafariBooking
                      ? `• Arrive at scheduled time • Collect your trail map at Explorer HQ (Adventure Playground) • Animal Park included — no separate ticket needed • Parking £4`
                      : `• Arrive at scheduled time • Parking £4 • Confirmation: ${customerDetails.email}`}
                  </div>
                </td>
              </tr>
            </table>

            {/* Terms */}
            <table cellPadding="0" cellSpacing="0" border="0" width="100%" style={{
              backgroundColor: colors.lightGray,
              borderTop: `1px solid ${colors.gold}`,
              borderRadius: '6px',
              marginBottom: '16px'
            }}>
              <tr>
                <td style={{ padding: '14px' }}>
                  <div style={{
                    fontSize: '12px',
                    color: colors.navyPrimary,
                    fontWeight: '700',
                    textTransform: 'uppercase',
                    marginBottom: '8px'
                  }}>
                    Terms
                  </div>
                  <div style={{
                    fontSize: '11px',
                    color: colors.textGray,
                    lineHeight: '1.5'
                  }}>
                    <strong>Non-refundable</strong> • Valid for date/time shown • Present at entrance
                  </div>
                  <div style={{
                    fontSize: '11px',
                    color: colors.textGray,
                    marginTop: '8px'
                  }}>
                    Questions: <strong>info@belvoircastle.com</strong> | <strong>01476 871002</strong>
                  </div>
                </td>
              </tr>
            </table>

            {/* Footer */}
            <table cellPadding="0" cellSpacing="0" border="0" width="100%" style={{
              borderTop: '1px solid #e0e0e0',
              marginTop: '16px',
              paddingTop: '16px'
            }}>
              <tr>
                <td align="center">
                  <div style={{
                    fontSize: '12px',
                    color: colors.navyPrimary,
                    fontWeight: '600',
                    marginBottom: '4px'
                  }}>
                    www.belvoircastle.com
                  </div>
                  <div style={{
                    fontSize: '11px',
                    color: colors.textGray
                  }}>
                    Belvoir Castle, Grantham, Leicestershire, NG32 1PE
                  </div>
                </td>
              </tr>
            </table>

          </td>
        </tr>
      </table>
    </div>
  );
};

export default EmailTicket;

